import React, { useState, memo } from "react";
import useTranslation from "next-translate/useTranslation";
import styles from "./GameButtons.module.css";
import { useGamePlay, offerDrawResponse } from "../../../redux/gamePlay";
import Popup from "../../../components/ui/popup";
import { GamePlayWs } from "../../../services/wsOutRoutes";
import GroupButton, {
  FIRST_BTN,
  MIDDLE_BTN,
  LAST_BTN,
  SINGLE_BTN,
} from "../../../components/ui/group-button";
import { Button } from "@/components/ui/button";
import { FaTrashAlt } from "react-icons/fa";

function sendResign() {
  GamePlayWs.sendResign();
}

function sendOfferDraw() {
  GamePlayWs.sendOfferDraw();
}

function sendAbort() {
  GamePlayWs.sendAbort();
}

function sendOfferDrawResponse(accept: boolean) {
  GamePlayWs.offerDrawResponse(accept);
  offerDrawResponse();
}

export default function GameButtons({
  isDesktop,
  setChatOpened,
  handleShowChatWindow,
  unreadChatMessages,
}) {
  const { t } = useTranslation("common");

  const { offerDrawReceived, showAbort } = useGamePlay();

  const [offerDrawStart, setOfferDrawStart] = useState(false);
  const [resignStart, setResignStart] = useState(false);
  const [abortStart, setAbortStart] = useState(false);
  const [offerDrawSent, setOfferDrawSent] = useState(false);

  function doSendOfferDraw() {
    setOfferDrawStart(false);
    setOfferDrawSent(true);
    sendOfferDraw();
  }

  function cancel() {
    setOfferDrawStart(false);
    setResignStart(false);
    setAbortStart(false);
  }

  function renderButtons(
    label1: string,
    action1: () => void,
    label2: string = null,
    action2: () => void = null
  ) {
    return (
      <div className="flex justify-center space-x-2">
        <button
          className="btn btn-sm btn-outline min-w-[80px]"
          onClick={action1}
        >
          {label1}
        </button>
        {label2 && action2 && (
          <button className="w-20 btn btn-sm btn-blue" onClick={action2}>
            {label2}
          </button>
        )}
      </div>
    );
  }

  function renderInfo(notPopup: boolean) {
    if (offerDrawReceived) {
      return (
        <div className={styles.gameInfo}>
          <div className={styles.gameInfoTitle}>
            {t("Your opponent has offered a draw Do you accept?")}
          </div>
          {renderButtons(
            t("Cancel"),
            () => sendOfferDrawResponse(false),
            t("Yes"),
            () => sendOfferDrawResponse(true)
          )}
        </div>
      );
    } else if (offerDrawStart) {
      return (
        <div className={styles.gameInfo}>
          {notPopup && (
            <div className="pb-2 font-semibold">{t("Offer draw")}</div>
          )}
          <div className={styles.gameInfoTitle}>{t("Are you sure?")}</div>
          {renderButtons(t("Cancel"), cancel, t("Yes"), doSendOfferDraw)}
        </div>
      );
    } else if (resignStart) {
      return (
        <div className={styles.gameInfo}>
          {notPopup && <div className="pb-2 font-semibold">{t("Resign")}</div>}
          <div className={styles.gameInfoTitle}>{t("Are you sure?")}</div>
          {renderButtons(t("Cancel"), cancel, t("Yes"), sendResign)}
        </div>
      );
    } else if (abortStart) {
      return (
        <div className={styles.gameInfo}>
          {notPopup && (
            <div className="pb-2 font-semibold">{t("Abort game")}</div>
          )}
          <div className={styles.gameInfoTitle}>{t("Are you sure?")}</div>
          {renderButtons(t("Cancel"), cancel, t("Yes"), sendAbort)}
        </div>
      );
    } else if (offerDrawSent) {
      return (
        <div className={styles.gameInfo}>
          <div className={styles.gameInfoTitle}>
            {t("You have offered a draw")}
          </div>
          <button
            className="w-20 btn btn-sm btn-outline"
            onClick={() => setOfferDrawSent(false)}
          >
            {t("Close")}
          </button>
        </div>
      );
    }
  }

  function renderPopup() {
    let title: string;
    if (offerDrawStart || offerDrawSent) {
      title = t("Offer draw");
    } else if (offerDrawReceived) {
      title = t("Draw");
    } else if (resignStart) {
      title = t("Resign");
    } else if (abortStart) {
      title = t("Abort game");
    }

    if (title) {
      return (
        <Popup title={title} onClose={cancel}>
          {renderInfo(false)}
        </Popup>
      );
    }
  }

  function renderDesktopButtons() {
    if (abortStart || resignStart || offerDrawStart || offerDrawSent) {
      return renderInfo(true);
    }

    return (
      <>
        <div className="flex gap-3 items-center">
          <Button
            onClick={() => setOfferDrawStart(true)}
            variant="outline"
            size="full"
          >
            {t("Offer draw")}
          </Button>
          <Button
            onClick={() => setResignStart(true)}
            variant="outline"
            size="full"
          >
            {t("Resign")}
          </Button>
        </div>
        {showAbort && (
          <div className="flex justify-center pt-2 mt-4 border-t border-gray-200 md:justify-start">
            <Button
              onClick={() => setAbortStart(true)}
              variant="ghost"
              size="sm"
              className="flex gap-2 items-center text-xs"
            >
              {/* <FaTrashAlt className="w-4 h-4" /> */}
              {t("Abort game")}
            </Button>
          </div>
        )}

        {renderInfo(true)}
      </>
    );
  }

  function renderMobileButtons() {
    return (
      <>
        <div
          className={`${
            offerDrawSent ? "hidden" : "flex justify-center items-center"
          }`}
        >
          <GroupButton
            buttonType={FIRST_BTN}
            onClick={() => setOfferDrawStart(true)}
          >
            {t("Offer draw")}
          </GroupButton>
          <GroupButton
            buttonType={MIDDLE_BTN}
            onClick={() => setResignStart(true)}
          >
            {t("Resign")}
          </GroupButton>
          <GroupButton
            buttonType={LAST_BTN}
            onClick={() => {
              setChatOpened(true);
              handleShowChatWindow(true);
            }}
            badgeNum={unreadChatMessages}
          >
            {t("Chat")}
          </GroupButton>
        </div>
        {showAbort && (
          <div className="flex justify-center pt-2 mt-4 border-t border-gray-200">
            <Button
              onClick={() => setAbortStart(true)}
              variant="ghost"
              size="sm"
              className="flex gap-2 items-center text-xs"
            >
              {/* <FaTrashAlt className="w-4 h-4" /> */}
              {t("Abort game")}
            </Button>
          </div>
        )}
        {renderPopup()}
      </>
    );
  }

  // function renderMobileButtons() {
  //   return (
  //     <>
  //       {/* <div
  //         className={`${
  //           offerDrawSent ? "hidden" : "flex justify-center items-center"
  //         }`}
  //       > */}
  //       <div className="px-4 pt-3">
  //         <div className="flex flex-col gap-2">
  //           <Button
  //             variant="outline"
  //             size="full"
  //             onClick={() => setOfferDrawStart(true)}
  //           >
  //             {t("Offer draw")}
  //           </Button>
  //           <Button
  //             variant="outline"
  //             size="full"
  //             onClick={() => setResignStart(true)}
  //           >
  //             {t("Resign")}
  //           </Button>
  //           <Button
  //             variant="outline"
  //             size="full"
  //             onClick={() => {
  //               setChatOpened(true);
  //               handleShowChatWindow(true);
  //             }}
  //             // badgeNum={unreadChatMessages}
  //           >
  //             {t("Chat")}
  //           </Button>
  //         </div>
  //       </div>
  //       {showAbort && (
  //         <div className="flex justify-center pt-2 mt-4 border-t border-gray-200">
  //           <Button
  //             onClick={() => setAbortStart(true)}
  //             variant="ghost"
  //             size="sm"
  //             className="flex gap-2 items-center text-xs"
  //           >
  //             {t("Abort game")}
  //           </Button>
  //         </div>
  //       )}
  //       {renderPopup()}
  //     </>
  //   );
  // }

  return (
    <div className="px-4 py-1 w-full">
      {isDesktop ? renderDesktopButtons() : renderMobileButtons()}
    </div>
  );
}
